import {
  ComponentPropsWithoutRef,
  forwardRef,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnPinningState,
  RowSelectionState,
  TableOptions,
  ExpandedState,
  getExpandedRowModel,
  getGroupedRowModel,
  SortingState,
} from "@tanstack/react-table";
import cx from "classnames";

import { LayoutUtilProps } from "../../../../types";
import { useLayoutPropsUtil } from "../../../../internal/hooks/useLayoutPropsUtil";

import Checkbox from "../../../../components/Checkbox";
import Button from "../../../../components/Button";
import Flex from "../../../../components/Flex";
import ExpandIcon from "../../../../../public/assets/icons/material/round/chevron_right.svg";

import { ColumnDef, CustomTableFooterCellProps, TableRow } from "../types";
import { TableBase } from "../base/TableBase";
import { DataTableBody } from "./internal/DataTableBody";
import { DataTableFooter } from "./internal/DataTableFooter";
import { getTanStackColumnDef } from "./internal/util/getTanStackColumnDef";
import { DataTableHeader } from "./internal/DataTableHeader";
import { TableContainer } from "../base/TableContainer";
import { DataTablePagination } from "./internal/DataTablePagination";
import { useOptionallyControlledState } from "../../../../internal";
import {
  DataTableCacheOptions,
  DataTableEmptyState,
  DataTablePaginationConfig,
  DataTableRef,
  SortedColumn,
} from "./types";

export type {
  DataTableCacheOptions,
  DataTableRef,
  DataTableEmptyState,
  SortedColumn,
};

import styles from "./DataTable.module.scss";
import { Spinner, SrOnly } from "../../../../components";
import { usePrefersColorScheme } from "../../../../hooks";
import { ThemeProviderContext } from "../../../../providers/ThemeProvider/internal/ThemeProviderContext";
import { useAsyncData } from "./internal/useAsyncData";
import { usePageDataCache } from "./internal/usePageDataCache";
import { DTHoverProvider } from "./internal/context/hover/DTHoverProvider";
import { DTFocusProvider } from "./internal/context/focus/DTFocusProvider";
import {
  INTERNAL_ROW_ACTION_PRIMARY_COLUMN_ID,
  INTERNAL_ROW_ACTION_SECONDARY_COLUMN_ID,
} from "./internal/constants";
import { CellStatus } from "./internal/cells/CellStatus";

const EMPTY_DATA: never[] = [];

type DataTableCellValue =
  | object
  | string
  | number
  | boolean
  | bigint
  | symbol
  | null
  | undefined;

export type DataTableRowData = Record<PropertyKey, DataTableCellValue>;

/**
 * Props for the DataTable component
 * @extends LayoutUtilProps
 * @extends ComponentPropsWithoutRef<"div">
 */
export type DataTableProps<T extends DataTableRowData> = LayoutUtilProps &
  ComponentPropsWithoutRef<"div"> & {
    /**
     * The columns for the table, created with the `createColumnHelper` factory function.
     */
    columns: ColumnDef<T>[];
    /**
     * The custom footer content for the table. Will be overridden by the footer content of the columns.
     */
    customFooter?: CustomTableFooterCellProps[][];
    /**
     * The data for the table. Must be an array of objects with an `id` property.
     */
    data?: TableRow<T>[] | Promise<TableRow<T>[]>;
    /**
     * The default expanded row ids. Can be "all" to expand all rows, or an array of row ids to expand specific rows.
     */
    defaultExpandedRowIds?: "all" | TableRow<T>["id"][];
    /**
     * Disables the "expand all" action in the table header.
     * When true, users can only expand individual rows.
     * @default false
     */
    disableExpandAll?: boolean;
    /**
     * The default selected row ids.
     */
    defaultSelectedRowIds?: TableRow<T>["id"][];
    /**
     * The default sorted column.
     */
    defaultSortedColumn?: SortedColumn;
    /**
     * The expanded row ids.
     */
    expandedRowIds?: "all" | TableRow<T>["id"][];
    /**
     * Disables the "select all" action in the table header.
     * When true, users can only select individual rows.
     * @default false
     */
    disableSelectAll?: boolean;
    /**
     * Whether the table is selectable. Can be a boolean or a function to customize the selection logic.
     * @default false
     */
    isSelectable?: boolean | ((row: T) => boolean);
    /**
     * Called when the row expansion state changes. Passes the expanded row ids.
     */
    onExpandRow?: (expandedRowIds: "all" | TableRow<T>["id"][]) => void;
    /**
     * Called when the row selection state changes. Passes the selected row ids.
     */
    onSelectRow?: (selectedRowIds: TableRow<T>["id"][]) => void;
    /**
     * Called when the column sorting state changes. Passes the sorted column.
     */
    onSort?: (sortedColumn: SortedColumn | undefined) => void;
    /**
     * Pagination configuration. When provided, enables pagination and filters data to show only the current page.
     * When `true`, the default pagination configuration is used.
     * When `false` or `undefined`, no pagination is applied and all data is shown.
     */
    pagination?: boolean | DataTablePaginationConfig<T>;
    /**
     * The selected row ids.
     */
    selectedRowIds?: TableRow<T>["id"][];
    /**
     * The sorted column.
     */
    sortedColumn?: SortedColumn;
    /**
     * Configuration for the empty state displayed when the table has no data.
     * Only shown when data is empty and the table is not loading.
     */
    emptyState?: DataTableEmptyState;
    /**
     * Custom content to display when a cell value is empty (null, undefined, or empty string).
     * Overrides the default em dash for all columns. Individual columns can further override
     * this with their own `emptyCellContent` property.
     */
    emptyCellContent?: ReactNode;
  };

function DataTableInner<T extends DataTableRowData>(
  props: DataTableProps<T>,
  ref: Ref<DataTableRef>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    data: dataProp,
    columns: columnsProp,
    customFooter,
    defaultExpandedRowIds,
    defaultSelectedRowIds,
    defaultSortedColumn,
    disableExpandAll,
    disableSelectAll,
    expandedRowIds,
    isSelectable,
    onSelectRow,
    onSort,
    onExpandRow,
    pagination,
    selectedRowIds,
    sortedColumn,
    emptyState,
    emptyCellContent,
    className,
    style,
    ...rest
  } = componentProps;
  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  const tableRef = useRef<HTMLDivElement>(null);

  const [rowSelection, setRowSelection] =
    useOptionallyControlledState<RowSelectionState>({
      controlledValue:
        isSelectable && selectedRowIds
          ? Object.fromEntries(selectedRowIds.map((id) => [id, true]))
          : undefined,
      defaultValue: isSelectable
        ? defaultSelectedRowIds
          ? Object.fromEntries(defaultSelectedRowIds.map((id) => [id, true]))
          : {}
        : {},
      onChange: (newRowSelection) => {
        onSelectRow?.(Object.keys(newRowSelection) as TableRow<T>["id"][]);
      },
    });

  const [sorting, setSorting] = useOptionallyControlledState<SortingState>({
    controlledValue: sortedColumn ? [sortedColumn] : undefined,
    defaultValue: defaultSortedColumn ? [defaultSortedColumn] : undefined,
    onChange: (newSorting) => {
      onSort?.(newSorting[0]);
    },
  });

  const isPaginated = !!pagination;
  const paginationObject =
    typeof pagination === "object" ? pagination : undefined;
  const rowsPerPage = paginationObject?.rowsPerPage ?? 25;
  const refreshKey = paginationObject?.refreshKey;

  const [currentPageIndex, setCurrentPageIndex] =
    useOptionallyControlledState<number>({
      controlledValue: paginationObject?.currentPageIndex,
      defaultValue: paginationObject?.defaultPageIndex ?? 0,
      onChange: (newPageIndex) => {
        paginationObject?.onPageChange?.(newPageIndex);
      },
    });

  const [refreshCounter, setRefreshCounter] = useState(0);

  const pageLoader = paginationObject?.loadPageData;
  const isAsyncPaginated = !!pageLoader && typeof pageLoader === "function";

  const { getOrLoad, clearCache } = usePageDataCache<T>({
    loadPageData: pageLoader,
    cache: paginationObject?.cache,
    refreshKey,
    refreshCounter,
    rowsPerPage,
    sorting: sorting?.[0],
  });

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        setRefreshCounter((c) => c + 1);
        setCurrentPageIndex(0);
      },
      clearCache,
    }),
    [setCurrentPageIndex, clearCache],
  );

  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip reset on initial render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Reset to page 0 when refreshKey changes
    if (refreshKey !== undefined) {
      setCurrentPageIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setCurrentPageIndex excluded to prevent reset on every page change
  }, [refreshKey]);

  const dataSource = useMemo(() => {
    if (!isAsyncPaginated) {
      return dataProp;
    }

    return getOrLoad(currentPageIndex ?? 0, rowsPerPage, sorting?.[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pageLoader omitted intentionally
  }, [
    isAsyncPaginated,
    currentPageIndex,
    rowsPerPage,
    dataProp,
    refreshKey,
    refreshCounter,
    getOrLoad,
    sorting,
  ]);

  const {
    data: resolvedData,
    //TODO: Table should represent error state
    error: _error,
    isLoading,
  } = useAsyncData(dataSource);
  const totalItemCount =
    paginationObject?.totalRowCount ?? resolvedData?.length ?? 0;

  const hasSubRows = !!resolvedData?.some(
    (row) => row?.subRows?.length ?? 0 > 0,
  );
  const hasSubComponent = !!resolvedData?.some(
    (row) => row?.subComponent != null,
  );
  const isExpandable = hasSubRows || hasSubComponent;

  const [expanded, setExpanded] = useOptionallyControlledState<ExpandedState>({
    controlledValue:
      isExpandable && expandedRowIds
        ? expandedRowIds === "all"
          ? true
          : Object.fromEntries(expandedRowIds.map((id) => [id, true]))
        : undefined,
    defaultValue: isExpandable
      ? defaultExpandedRowIds
        ? defaultExpandedRowIds === "all"
          ? true
          : Object.fromEntries(defaultExpandedRowIds.map((id) => [id, true]))
        : {}
      : {},
    onChange: (newExpanded) => {
      if (newExpanded === true) {
        onExpandRow?.("all");
      } else {
        onExpandRow?.(Object.keys(newExpanded) as TableRow<T>["id"][]);
      }
    },
  });

  // Full data passed to TanStack — getPaginationRowModel slices after sorting.
  const tableData = isLoading ? EMPTY_DATA : resolvedData;

  // Check if any row has a row-level error or warning
  const hasRowStatus = useMemo(() => {
    return tableData?.some((row) => {
      const meta = (row as TableRow<T>).meta;
      return meta?.rowError || meta?.rowWarning;
    });
  }, [tableData]);

  // Calculate column pinning state (including select/expand/row-error columns if needed)
  const columnPinning = useMemo((): ColumnPinningState => {
    const { left, right } = {
      left: columnsProp
        .filter((column) => column.pinned === "left")
        .map((c) => String(c.id)),
      right: columnsProp
        .filter((column) => column.pinned === "right")
        .map((c) => String(c.id)),
    };

    // If any are pinned left, we want to also pin select/expand/row-status columns
    const hasInternalColumns = isSelectable || isExpandable || hasRowStatus;
    const standardPinnedColumns = [];
    if (isSelectable) {
      standardPinnedColumns.push(INTERNAL_ROW_ACTION_SECONDARY_COLUMN_ID);
    }
    if (isExpandable) {
      standardPinnedColumns.push(INTERNAL_ROW_ACTION_PRIMARY_COLUMN_ID);
    }
    if (hasRowStatus) {
      standardPinnedColumns.push("a2-table-internal-row-status");
    }

    return {
      left:
        hasInternalColumns && left && left.length > 0
          ? [...standardPinnedColumns, ...left]
          : (left ?? []),
      right: right ?? [],
    };
  }, [columnsProp, hasRowStatus, isSelectable, isExpandable]);

  // Build columns for TanStack useReactTable
  const columns = useMemo(() => {
    const tableColumns: TableOptions<T>["columns"] = columnsProp.map((column) =>
      getTanStackColumnDef({
        columnDef: column,
        emptyCellContent,
      }),
    );

    // Add row status column if any row has a row-level error or warning
    // This goes after expand/select columns but before data columns
    if (hasRowStatus) {
      tableColumns.unshift({
        id: "a2-table-internal-row-status",
        size: 32,
        maxSize: 32,
        minSize: 32,
        header: () => null,
        cell: ({ row }) => {
          const meta = (row.original as TableRow<T>).meta;
          const rowError = meta?.rowError;
          const rowWarning = meta?.rowWarning;
          if (rowError) return <CellStatus status={rowError} variant="error" />;
          if (rowWarning)
            return <CellStatus status={rowWarning} variant="warning" />;
          return null;
        },
      });
    }

    // Add expand column if subRows are present
    if (isExpandable) {
      tableColumns.unshift({
        id: INTERNAL_ROW_ACTION_PRIMARY_COLUMN_ID,
        size: 44,
        maxSize: 44,
        minSize: 44,
        meta: { disableHeaderAction: disableExpandAll },
        header: ({ table }) => {
          if (disableExpandAll) return null;
          return (
            <Flex grow={1} justifyContent="center">
              <Button
                tabIndex={-1}
                onClick={table.getToggleAllRowsExpandedHandler()}
                appearance="ghost"
                className={cx(styles["expand-button"], {
                  [styles["expanded"]]: table.getIsAllRowsExpanded(),
                })}
                size="small"
                icon={ExpandIcon}
                aria-label={
                  table.getIsAllRowsExpanded()
                    ? "Collapse all rows"
                    : "Expand all rows"
                }
                data-cell-action="expand"
              />
            </Flex>
          );
        },
        cell: ({ row }) => {
          if (row.getCanExpand()) {
            return (
              <Button
                tabIndex={-1}
                onClick={row.getToggleExpandedHandler()}
                appearance="ghost"
                size="small"
                icon={ExpandIcon}
                className={cx(styles["expand-button"], {
                  [styles["expanded"]]: row.getIsExpanded(),
                })}
                aria-label={row.getIsExpanded() ? "Collapse row" : "Expand row"}
                aria-expanded={
                  hasSubComponent && row.getCanExpand()
                    ? row.getIsExpanded()
                    : undefined
                }
                aria-controls={
                  hasSubComponent && row.getCanExpand()
                    ? `${row.id}-sub-component`
                    : undefined
                }
                data-cell-action="expand"
              />
            );
            // show a checkbox for nested rows if isSelectable is true
          } else if (row.getParentRow()?.getCanExpand() && isSelectable) {
            return (
              <Checkbox
                tabIndex={-1}
                aria-label={row.getIsSelected() ? "Deselect row" : "Select row"}
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                indeterminate={row.getIsSomeSelected()}
                onChange={row.getToggleSelectedHandler()}
                data-cell-action="select"
              />
            );
          }
        },
      });
    }

    // Add select column if isSelectable is true
    if (isSelectable) {
      tableColumns.unshift({
        id: INTERNAL_ROW_ACTION_SECONDARY_COLUMN_ID,
        size: 44,
        maxSize: 44,
        minSize: 44,
        meta: { disableHeaderAction: disableSelectAll },
        header: ({ table }) => {
          if (disableSelectAll) return null;
          return (
            <Flex grow={1} justifyContent="center">
              <Checkbox
                tabIndex={-1}
                aria-label={
                  table.getIsAllPageRowsSelected()
                    ? "Deselect all rows"
                    : "Select all rows"
                }
                checked={table.getIsAllPageRowsSelected()}
                indeterminate={table.getIsSomePageRowsSelected()}
                onChange={table.getToggleAllPageRowsSelectedHandler()}
                data-cell-action="select"
              />
            </Flex>
          );
        },
        cell: ({ row }) => {
          if (row.getParentRow()?.getCanExpand() && isSelectable) {
            return null;
          }
          return (
            <Checkbox
              tabIndex={-1}
              aria-label={row.getIsSelected() ? "Deselect row" : "Select row"}
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
              data-cell-action="select"
            />
          );
        },
      });
    }

    return tableColumns;
  }, [
    columnsProp,
    disableExpandAll,
    disableSelectAll,
    emptyCellContent,
    hasRowStatus,
    hasSubComponent,
    isExpandable,
    isSelectable,
  ]);

  const table = useReactTable({
    data: tableData ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getRowId: (row) => (row as TableRow<T>).id.toString(),
    getSubRows: (row) => (row as TableRow<T>).subRows ?? undefined,
    getRowCanExpand: (row) => {
      const originalRow = row.original as TableRow<T>;
      return (
        (originalRow?.subRows?.length ?? 0) > 0 ||
        originalRow?.subComponent != null
      );
    },
    state: {
      columnPinning,
      rowSelection,
      expanded,
      sorting,
      ...(isPaginated && {
        pagination: {
          pageIndex: currentPageIndex ?? 0,
          pageSize: rowsPerPage,
        },
      }),
    },
    manualPagination: !isPaginated || isAsyncPaginated,
    // Server provides sorted data via loadPageData; disable client-side sort.
    manualSorting: isAsyncPaginated,
    autoResetPageIndex: false,
    columnResizeMode: "onChange",
    enableRowSelection:
      typeof isSelectable === "function"
        ? (row) => isSelectable(row.original)
        : isSelectable,
    onRowSelectionChange: (newRowSelection) => {
      setRowSelection(newRowSelection as RowSelectionState);
    },
    onExpandedChange: (newExpanded) => {
      setExpanded(newExpanded as ExpandedState);
    },
    onSortingChange: (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      const sortingChanged =
        JSON.stringify(newSorting) !== JSON.stringify(sorting);
      if (!sortingChanged) {
        return;
      }

      setSorting(newSorting);
      if (isPaginated) {
        setCurrentPageIndex(0);
      }
    },
  });

  const headers = table.getFlatHeaders();
  const headerRowCount = table.getHeaderGroups().length;
  const bodyRowCount = table.getRowModel().rows.length;
  const columnSizing = table.getState().columnSizing;

  const getMaxFooterRows = useCallback((column: ColumnDef<T>): number => {
    let currentMax = 0;

    // Check current column's footer content
    if (Array.isArray(column.footerContent)) {
      currentMax = column.footerContent.length;
    } else if (column.footerContent) {
      currentMax = 1;
    }

    // Recursively check nested columns and find the maximum
    if (column.columns && Array.isArray(column.columns)) {
      const nestedMax = Math.max(
        ...column.columns.map((nestedCol) => getMaxFooterRows(nestedCol)),
        0,
      );
      currentMax = Math.max(currentMax, nestedMax);
    }

    return currentMax;
  }, []);

  const footerLength = useMemo(() => {
    return Math.max(
      ...columnsProp.map((column) => getMaxFooterRows(column)),
      0,
    );
  }, [columnsProp, getMaxFooterRows]);

  const hasFooters =
    footerLength > 0 || (customFooter !== undefined && customFooter.length > 0);

  const rowCount = useMemo(
    () => bodyRowCount + headerRowCount + footerLength,
    [bodyRowCount, headerRowCount, footerLength],
  );

  const isEmpty = !isLoading && (!tableData || tableData.length === 0);

  const { mode: themeMode } = useContext(ThemeProviderContext);
  const { mode: sysMode } = usePrefersColorScheme();
  const colorSchemeMode = themeMode ?? sysMode;

  const EmptyStateSvg = (() => {
    const svg = emptyState?.svg;
    if (!svg) return undefined;
    if (typeof svg === "function") return svg;
    return colorSchemeMode === "dark"
      ? (svg.dark ?? svg.light)
      : (svg.light ?? svg.dark);
  })();

  /**
   * Create CSS variables for the column sizes and grid template
   */
  const cssVars = useMemo(() => {
    const vars: { [key: string]: number | string } = {};

    // Get leaf columns (non-grouped) in visual order
    const leafHeaders = headers.filter(
      (header) => header.subHeaders?.length === 0,
    );

    // Check if all columns have maxWidth defined
    const allHaveMaxWidth = leafHeaders.every(
      (header) => header.column.columnDef.maxSize != null,
    );

    // Build grid-template-columns from leaf column sizes using minmax()
    // Columns expand to fill available space up to their maxWidth
    const gridColumns = leafHeaders
      .map((header) => {
        const columnId = header.column.id;
        const hasBeenResized = columnId in columnSizing;
        const currentSize = header.column.getSize();

        // If user has manually resized this column, lock it to that size
        if (hasBeenResized) {
          return `${currentSize}px`;
        }

        // Otherwise, allow expansion up to maxWidth (or 1fr if no max)
        const minSize = header.column.columnDef.minSize ?? currentSize;
        const maxSize = header.column.columnDef.maxSize;
        return `minmax(${minSize}px, ${maxSize ? `${maxSize}px` : "1fr"})`;
      })
      .join(" ");
    vars["--a2-grid-template-columns"] = gridColumns;

    // Set table width: 100% if any column can expand (no maxWidth), otherwise auto
    vars["--a2-table-width"] = allHaveMaxWidth ? "max-content" : "100%";

    // Keep individual column size variables for other uses (resizing, etc.)
    for (const header of headers) {
      vars[`--a2-table-col-${header.column.getIndex()}-size`] =
        header.column.getSize();
    }
    return vars;
  }, [headers, columnSizing]);

  const columnCount = useMemo(() => {
    // don't count grouped column header cells
    return headers.filter((header) => header.subHeaders?.length === 0).length;
  }, [headers]);

  return (
    <DTFocusProvider
      rowCount={rowCount}
      columnCount={columnCount}
      tableRef={tableRef}
      leftPinnedWidth={table.getLeftTotalSize()}
      rightPinnedWidth={table.getRightTotalSize()}
    >
      <DTHoverProvider>
        <TableContainer
          {...rest}
          ref={tableRef}
          style={{
            ...styleCombined,
            width: cssVars["--a2-table-width"],
          }}
          className={cx(className, {
            [styles["container-with-pagination"]]: pagination,
          })}
        >
          <TableBase
            type="data-table"
            style={cssVars}
            rowCount={rowCount}
            colCount={columnCount}
            hasSubRows={hasSubRows}
          >
            <DataTableHeader table={table} />
            <DataTableBody table={table} hasSubRows={hasSubRows} />
            {isEmpty && emptyState && (
              <>
                <SrOnly>No data to display.</SrOnly>
                <div className={styles["empty-state-container"]}>
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    direction="column"
                    gap={2}
                  >
                    {EmptyStateSvg && <EmptyStateSvg aria-hidden="true" />}
                    {emptyState.content}
                  </Flex>
                </div>
              </>
            )}
            {isLoading && (
              <>
                <SrOnly>Loading data. Please wait.</SrOnly>
                {/* Visual spacer to avoid collapsing the table body area */}
                <div
                  style={{
                    height: rowsPerPage ? `${rowsPerPage * 35}px` : "200px",
                  }}
                  role="presentation"
                  aria-hidden="true"
                >
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: 0,
                      right: 0,
                      margin: "auto",
                      zIndex: 3,
                    }}
                  >
                    <Spinner />
                  </Flex>
                </div>
              </>
            )}
            {hasFooters && (
              <DataTableFooter
                className={
                  pagination ? styles["footer-with-pagination"] : undefined
                }
                table={table}
                columns={columnsProp}
                customFooter={customFooter}
              />
            )}
          </TableBase>
          {pagination && (
            <DataTablePagination
              itemsPerPage={rowsPerPage}
              showCount={paginationObject?.showCount ?? true}
              page={(currentPageIndex ?? 0) + 1}
              totalItemCount={totalItemCount}
              onPageChange={(page) => {
                setCurrentPageIndex(page - 1);
              }}
              onItemsPerPageChange={(itemsPerPage) => {
                paginationObject?.onRowsPerPageChange?.(itemsPerPage);
              }}
              itemsPerPageOptions={paginationObject?.rowsPerPageOptions}
            />
          )}
        </TableContainer>
      </DTHoverProvider>
    </DTFocusProvider>
  );
}

/**
 * Data table component for complex data grid layouts
 *
 * Features:
 * - Built-in focus management for accessibility
 * - Column resizing
 * - Column pinning
 * - Column grouping
 * - Controlled or uncontrolled column sorting
 * - Row selection
 * - Row expansion
 * - Row sub-component
 * - Row sub-rows
 * - Default and Strong backgrounds
 * - Pagination support with configurable items per page
 * - Imperative refresh via ref
 * - Customizable empty state with optional SVG illustration and content
 *
 * @param props - The props for the DataTable component
 * @param props.data - The data for the table. Must be an array of objects with an `id` property.
 * @param props.columns - The columns for the table, created with the `createColumnHelper` factory function.
 * @param props.customFooter - The custom footer content for the table. Will be overridden by the footer content of the columns.
 * @param props.defaultExpandedRowIds - The default expanded row ids.
 * @param props.defaultSelectedRowIds - The default selected row ids.
 * @param props.defaultSortedColumn - The default sorted column.
 * @param props.expandedRowIds - The expanded row ids.
 * @param props.isSelectable - Whether the table is selectable. Can be a boolean or a function to customize the selection logic.
 * @param props.onSelectRow - Called when the row selection state changes. Passes the selected row ids.
 * @param props.onSort - Called when the column sorting state changes. Passes the sorted column.
 * @param props.onExpandRow - Called when the row expansion state changes. Passes the expanded row ids.
 * @param props.pagination - Pagination configuration. When provided, enables pagination and filters data to show only the current page. When undefined, all data is shown and no pagination controls are rendered.
 * @param props.selectedRowIds - The selected row ids.
 * @param props.sortedColumn - The sorted column.
 * @param props.emptyState - Configuration for the empty state displayed when the table has no data.
 * @param props.style - The style of the table container.
 * @param props.rest - The rest of the props for the DataTable component, spread to the div surrounding the table.
 *
 * @example
 * <DataTable
 *   data={tableData}
 *   columns={columns}
 *   pagination={{
 *     itemsPerPage: 25,
 *     defaultPageIndex: 0,
 *     onPageChange: (pageIndex) => console.log('Page changed to:', pageIndex),
 *     showCount: true,
 *   }}
 * />
 */
type DataTableComponent = <T extends DataTableRowData>(
  props: DataTableProps<T> & { ref?: Ref<DataTableRef> },
) => ReturnType<typeof DataTableInner>;

export const DataTable = forwardRef(DataTableInner) as DataTableComponent;
